// The windows of a Google Drive connector of the office's library (catalogue id `gdrive`: one
// Google account and one folder of its Drive per connector, server/connectors/plugins/gdrive.js and
// server/modules/drive.js), opened from « Connecteurs du bureau » and Administration → Connecteurs
// (myconnectors/plugin.js hands them the `gdrive` card and settings). Adding one works like Shopify:
// - « Ajouter Google Drive » (openDriveAdd): a name (optional: named after the Google account), then
//   AJOUTER: the connector is added (POST /api/connectors/library {catalogId: 'gdrive', label?}), its
//   card appears in the library and the caller opens its connect window (`onAdded`).
// - Its connect and settings window (openDriveSettings; whoever may manage it — an administrator,
//   the person who added it), a 2-step stepper until it is ready:
//   1 · « Connecter le compte Google »: the access as two option cards — « Limité à un dossier »
//   (scope drive.file, recommended) or « Tout le Drive » (scope drive) — and « SE CONNECTER AVEC
//   GOOGLE »: the server gives Google's consent page (POST …/:id/google/start {mode}) and the browser
//   goes there, only when it is exactly Google's (safeGoogleUrl). Google brings it back to
//   #/connectors/<id>?drive=… (web/ui/myconnectors.js says how it went and opens this window again
//   on step 2 while it has no folder).
//   2 · « Choisir le dossier »: Google's picker in a popup (the page …/google/picker posts {type:
//   PICKER_MESSAGE, connectorId, outcome, folder} back from the same origin; the popup closed is read
//   again too); in « Tout le Drive » also a pasted link. Until it has its folder, that is the only
//   task shown (the office's files and its transfers come after).
//   Once connected with its folder: a summary (« Accès limité au dossier « … » », the account, Ouvrir
//   dans Drive, Changer de dossier), for administrators the switch « Stocker les fichiers du bureau
//   ici » (one connector at a time: the former one keeps its files, readable), its transfers (what is
//   still on the server, the last pass, problems, TRANSFÉRER MAINTENANT, METTRE EN PAUSE / REPRENDRE;
//   read again every few seconds while a pass runs), then « Changer l’accès » / « Reconnecter ».
// The why of each step sits in « ? » tooltips (infoTip), never in paragraphs. Renommer / Retirer stay
// on the connector's row. Nothing here ever holds a token: the server keeps them; the picker page has
// its own.
import { h, button, input, field, badge, switchToggle, uid, icon, infoTip } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { relativeTime, formatDateTime } from '../../format.js';
import { errorText } from '../admin/model.js';
import { alertBox, formError, setBusy, focusKeeper } from '../admin/common.js';
import { LIBRARY_BASE, LABEL_MAX, connectorOf, normalizeLibrary, validateLabel, labelTaken } from './model.js';
import {
  DRIVE_MODES, OFFICE_FOLDER, PICKER_MESSAGE, driveConnectorOf, driveState, driveStateBadge, driveNeedsFolder, driveFolderLink, pendingOf,
  pendingText, doneText, quotaText, accountText, safeGoogleUrl,
} from './drive-model.js';
import './strings.js';

/** « Se connecter avec Google » asks the server for Google's address (no Google call). */
const START_TIMEOUT_MS = 20_000;
/** Adding the connector (no provider call). */
const ADD_TIMEOUT_MS = 30_000;
/** While a transfer runs, the connector is read again this often. */
export const SWEEP_POLL_MS = 3000;
/** The picker's popup is checked this often (closed: the connector is read again). */
const POPUP_POLL_MS = 1000;
/** Administration → Connecteurs, where the « Application Google » block is. */
const ADMIN_CONNECTORS_HASH = '#/admin/connectors';
const NO_AUTOFILL = { autocomplete: 'off', autocapitalize: 'none', spellcheck: false, 'data-1p-ignore': '', 'data-lpignore': 'true', 'data-bwignore': '' };
const enc = encodeURIComponent;
const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/** The browser goes to Google (tests pass their own). */
function defaultNavigate(url) {
  globalThis.location?.assign?.(url);
}

/** Safari (not a Chromium or Firefox browser carrying « Safari » in its name). */
export function isSafari(ua = globalThis.navigator?.userAgent) {
  const s = String(ua || '');
  return /Safari\//.test(s) && !/(Chrome|Chromium|CriOS|FxiOS|EdgiOS|Edg|OPR)\//.test(s);
}

/** The picker page in a popup (it needs its opener: never noopener). */
function defaultOpenWindow(url) {
  return globalThis.open?.(url, 'maeck-drive-picker', 'popup,width=760,height=680') ?? null;
}

/**
 * « Se connecter avec Google »: Google's consent page from the server (a state bound to this
 * person, this browser, the connector and the access), then the browser goes there. False (and a
 * toast) when the address is not exactly Google's consent page.
 * @param {object} ctx
 * @param {{id: string}} conn
 * @param {{mode?: 'limited'|'full', navigate?: (url: string) => void}} [opts]
 * @returns {Promise<boolean>}
 */
export async function startGoogle(ctx, conn, { mode, navigate = ctx.navigateTo || defaultNavigate } = {}) {
  const res = await ctx.api.post(`${LIBRARY_BASE}/${enc(conn.id)}/google/start`, DRIVE_MODES.includes(mode) ? { mode } : {}, { timeoutMs: START_TIMEOUT_MS });
  const url = safeGoogleUrl(res?.url);
  if (!url) {
    ctx.toast.error(t('pc.drive.badUrl'));
    return false;
  }
  ctx.toast.info(t('pc.drive.redirecting'));
  navigate(url);
  return true;
}

/** A small secondary button opening a Google page in a new tab. */
function extButton(href, label) {
  return h('a', { class: 'btn btn-secondary btn-sm pc-drive-ext', href, target: '_blank', rel: 'noopener noreferrer' },
    icon('external', { size: 16 }), h('span', { class: 'btn-label' }, label), h('span', { class: 'sr-only' }, ` ${t('pc.drive.newTab')}`));
}

/** A title with its « ? » (the why, never on the page). */
function titled(tag, cls, text, tip) {
  return h(tag, { class: cls }, h('span', {}, text), tip ? infoTip(tip) : null);
}

/**
 * One step of the stepper: its numbered circle (a check once done), its title and its body.
 * @param {number} n
 * @param {'active'|'done'|'locked'} state locked: greyed until the step before is done
 */
function step(n, state, title, tip, ...body) {
  return h('li', { class: ['pc-drive-step', `is-${state}`], 'aria-current': state === 'active' ? 'step' : null },
    h('span', { class: 'pc-drive-step-num', 'aria-hidden': 'true' }, state === 'done' ? icon('check', { size: 14, stroke: 2.4 }) : String(n)),
    h('div', { class: 'pc-drive-step-body' }, titled('h4', 'pc-drive-step-title', title, tip), ...body));
}

/**
 * The two accesses as option cards (a radio, a title, one line, a « ? »; the browser's grouping is
 * not relied upon).
 * @param {string} initial
 * @param {(m: 'limited'|'full') => void} [onChange] another access chosen
 * @returns {{el: HTMLElement, value: () => 'limited'|'full', set: (m: string) => void}}
 */
function modeChooser(initial = 'limited', onChange = () => {}) {
  const name = uid('pc-drive-mode');
  let chosen = DRIVE_MODES.includes(initial) ? initial : 'limited';
  const radios = DRIVE_MODES.map((m) => h('input', {
    type: 'radio', name, value: m, class: 'pc-drive-radio', checked: m === chosen,
    onChange: (e) => {
      if (!e?.target?.checked) return;
      chosen = m;
      for (const r of radios) if (r.value !== m) r.checked = false;
      onChange(m);
    },
  }));
  const el = h('fieldset', { class: 'pc-drive-modes' },
    h('legend', { class: 'sr-only' }, t('pc.drive.modeLabel')),
    DRIVE_MODES.map((m, i) => h('div', { class: 'pc-drive-mode', dataset: { mode: m } },
      h('label', { class: 'pc-drive-mode-main' }, radios[i],
        h('span', { class: 'pc-drive-mode-text' },
          h('span', { class: 'pc-drive-mode-name' }, t(`pc.drive.mode.${m}`)),
          h('span', { class: 'pc-drive-mode-hint' }, t(`pc.drive.mode.${m}Hint`)))),
      infoTip(t(`pc.drive.mode.${m}Tip`), { class: 'pc-drive-mode-tip', label: t('pc.drive.tipAria', { name: t(`pc.drive.modeName.${m}`) }) }))));
  const set = (m) => {
    chosen = DRIVE_MODES.includes(m) ? m : 'limited';
    for (const r of radios) r.checked = r.value === chosen;
  };
  return { el, value: () => chosen, set };
}

/**
 * The note of a Google Drive window while the office's Google app (or its API key) is not set; an
 * administrator gets a shortcut to its settings (the window closes first).
 */
function noAppNote(ctx, layerOf, text = t('pc.drive.noApp')) {
  const admin = ctx.store?.get?.()?.me?.role === 'admin';
  return h('div', { class: 'stack pc-plugin-noapp' },
    alertBox('amber', text),
    admin ? button(t('pc.drive.noAppAdmin'), {
      variant: 'secondary', size: 'sm', icon: 'settings', class: 'pc-plugin-noapp-go',
      onClick: () => { layerOf()?.close?.('button'); ctx.navigate?.(ADMIN_CONNECTORS_HASH); },
    }) : null);
}

/**
 * « Ajouter Google Drive »: a name (optional), then AJOUTER adds the connector (« à connecter »);
 * the caller opens its connect window (`onAdded`), like a Shopify store's.
 * @param {object} ctx app context
 * @param {{id: string, label: string, description?: string}} entry the catalogue's `gdrive` card
 * @param {{data?: object|null, list?: object[], onAdded?: (c: object) => void}} [opts]
 *   data: normalizeLibrary() (the office's Google app ready or not); list: the whole library (names
 *   taken); onAdded: the new connector
 * @returns {import('../overlay.js').Layer}
 */
export function openDriveAdd(ctx, entry, { data = null, list = [], onAdded = () => {} } = {}) {
  // Without the library's answer the server decides (it refuses without the app).
  const ready = data ? data.apps?.google?.ready === true : true;
  let layer = null;
  let busy = false;
  const labelInput = input({ name: 'pc-drive-label', maxLength: LABEL_MAX, placeholder: t('pc.drive.namePlaceholder'), autocomplete: 'off' });
  const labelField = field(t('pc.add.nameLabel'), labelInput, { hint: t('pc.drive.nameHint'), optional: true });
  const errorBox = formError();
  const submit = button(t('pc.addCat.submit'), { variant: 'primary', type: 'submit', icon: 'plus', disabled: !ready });
  const body = h('form', { class: 'stack pc-plugin-form pc-drive-add', novalidate: true, onSubmit: (e) => { e?.preventDefault?.(); send(); } },
    entry.description ? h('p', { class: 'pc-plugin-desc muted' }, entry.description) : null,
    labelField,
    ready ? null : noAppNote(ctx, () => layer),
    errorBox,
    h('div', { class: 'pc-plugin-foot' }, submit));

  async function send() {
    if (busy || !ready) return;
    errorBox.set('');
    labelField.setError(null);
    const raw = String(labelInput.value ?? '').trim();
    let label = null;
    if (raw) {
      const v = validateLabel(raw);
      const error = !v.ok ? v.error : labelTaken(v.value, list) ? t('pc.err.labelTaken', { label: v.value }) : null;
      if (error) {
        labelField.setError(error);
        labelInput.focus?.();
        return;
      }
      label = v.value;
    }
    busy = true;
    setBusy(submit, true);
    try {
      const saved = connectorOf(await ctx.api.post(LIBRARY_BASE, { catalogId: entry.id, ...(label ? { label } : {}) }, { timeoutMs: ADD_TIMEOUT_MS }));
      if (!saved) throw new Error(t('pc.gone'));
      layer?.close('done');
      ctx.toast.success(t('pc.add.added', { name: saved.label }));
      onAdded(saved);
    } catch (err) {
      if (!layer?.isOpen?.()) ctx.toast.error(errorText(err));
      else if (err?.details?.field === 'label') {
        labelField.setError(errorText(err));
        labelInput.focus?.();
      } else errorBox.set(errorText(err));
    } finally {
      busy = false;
      setBusy(submit, false);
    }
  }

  const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('button') });
  layer = ctx.overlay.openModal({
    title: t('pc.addCat.title', { name: entry.label }), subtitle: t('pc.addCat.subtitle.gdrive'), size: 'md',
    content: body, footer: [cancel], className: 'adm-modal pc-modal pc-plugin-modal pc-drive-modal',
  });
  labelInput.focus?.();
  return layer;
}

/**
 * The connect and settings window of a Google Drive connector (whoever may manage it; the storage
 * switch, the transfers' buttons: administrators). Reads its connection (GET …/:id/google/status)
 * and the library (the office's Google app and picker ready, which connector stores the office's
 * files now).
 * @param {object} ctx app context
 * @param {{id: string, label?: string}} conn
 * @param {{onChanged?: () => void, navigate?: (url: string) => void, openWindow?: (url: string) => Window|null}} [opts]
 *   onChanged: its folder or the storage changed (the caller reads the library again); navigate /
 *   openWindow: tests (else ctx.navigateTo / ctx.openWindow, else location.assign / window.open)
 * @returns {import('../overlay.js').Layer}
 */
export function openDriveSettings(ctx, conn, { onChanged = () => {}, navigate, openWindow } = {}) {
  const id = conn.id;
  const path = `${LIBRARY_BASE}/${enc(id)}/google`;
  const isAdmin = () => ctx.store?.get?.()?.me?.role === 'admin' && ctx.store.get().me.disabled !== true;
  let layer = null;
  let view = null; // driveConnectorOf()
  let lib = null; // normalizeLibrary(): the Google app, its picker, the other connectors' storage
  let error = null;
  let busy = false;
  let changing = false; // « Changer l’accès »: the chooser while connected
  let pasteShown = false; // « Tout le Drive » with a folder: the pasted link asked for
  let destroyed = false;
  let pollTimer = null;
  let popup = null;
  let popupTimer = null;
  const keeper = focusKeeper();
  // « Tout le Drive »: Google warns about an app it has not checked; said before going there.
  const fullWarn = h('p', { class: 'pc-drive-line is-warn pc-drive-fullwarn' }, icon('alert', { size: 14 }), h('span', {}, t('pc.drive.fullWarn')));
  const modes = modeChooser(conn.plugin?.mode || 'limited', (m) => { fullWarn.hidden = m !== 'full'; });
  const pasteInput = input({ name: 'pc-drive-folder-link', inputmode: 'url', maxLength: 2000, placeholder: 'https://drive.google.com/drive/folders/…', ...NO_AUTOFILL });
  const pasteField = field(t('pc.drive.paste'), pasteInput);
  const content = h('div', {
    class: 'stack pc-drive',
    // Enter in the pasted link sends it (nothing else here is a form).
    onKeydown: (e) => {
      if (e?.key !== 'Enter' || e.target !== pasteInput) return;
      e.preventDefault?.();
      sendLink();
    },
  }, h('p', { class: 'muted' }, t('pc.loading')));

  const name = () => view?.label || conn.label || id;
  const appReady = () => (lib ? lib.apps.google.ready === true : true);
  const pickerReady = () => (lib ? lib.apps.google.pickerReady === true : true);
  const otherStorage = () => (lib?.connectors || []).find((c) => c.id !== id && c.catalogId === 'gdrive' && c.storage === true) || null;
  const line = (text, cls = 'muted') => h('p', { class: ['pc-drive-line', cls] }, text);

  function act(key, label, opts) {
    return keeper.reg(`drive-${key}:${id}`, button(label, { size: 'sm', disabled: busy, ...opts }));
  }

  function changed() {
    try { onChanged(); } catch (err) { console.error(err); }
  }

  // --- rendering --------------------------------------------------------------------------------
  function headPart(state) {
    // Its steps say where it stands while it is not ready (no folder yet): a badge only for the office's files.
    if ((state === 'off' || driveNeedsFolder(view)) && !view.storage) return null;
    const tag = driveStateBadge(view);
    return h('div', { class: 'pc-drive-head' }, badge(t(tag.key), tag.tone),
      view.storage ? badge(t('pc.drive.storageBadge'), 'info', { title: t('pc.drive.storageBadgeTitle') }) : null);
  }

  /** « SE CONNECTER AVEC GOOGLE » (or RECONNECTER) with the access chosen above it. */
  function connectButtons(labelKey) {
    const ready = appReady();
    fullWarn.hidden = modes.value() !== 'full';
    return [
      modes.el,
      ready ? null : noAppNote(ctx, () => layer),
      h('div', { class: 'pc-drive-actions' },
        act('connect', t(labelKey), { variant: 'primary', size: undefined, icon: 'key', disabled: busy || !ready, onClick: () => connect(modes.value()) }),
        changing ? act('cancel', t('common.cancel'), { variant: 'ghost', size: undefined, onClick: () => { changing = false; render(); } }) : null),
      ready ? fullWarn : null,
      ready ? line(t('pc.drive.connectHint')) : null,
    ];
  }

  /** Never connected or refused by Google: step 1 to do, step 2 greyed. */
  function setupSteps(state) {
    const refused = state === 'reconnect';
    return h('ol', { class: 'pc-drive-steps' },
      step(1, 'active', t('pc.drive.step1'), t('pc.drive.step1Tip'),
        refused ? alertBox('amber', t('pc.drive.reconnectText'), infoTip(t('pc.drive.reconnectTip'))) : null,
        view.lastError ? alertBox('danger', t('pc.drive.lastError', { error: view.lastError })) : null,
        refused && view.account ? line(t('pc.drive.accountIs', { account: accountText(view.account) }), 'is-strong') : null,
        refused && (view.done.files || view.storage) ? h('p', { class: 'pc-drive-line is-warn' }, t('pc.drive.sameAccount'), infoTip(t('pc.drive.sameAccountTip'))) : null,
        ...connectButtons(refused ? 'pc.drive.reconnect' : 'pc.drive.connect')),
      step(2, 'locked', t('pc.drive.step2'), null));
  }

  /** « Changer l’accès » while connected: the other access, Google asks again. */
  function changePart() {
    return h('section', { class: 'pc-drive-part pc-drive-change' },
      titled('h4', 'pc-drive-step-title', t('pc.drive.change'), t('pc.drive.changeTip')),
      line(t('pc.drive.changeHint')),
      view.account ? line(t('pc.drive.accountIs', { account: accountText(view.account) }), 'is-strong') : null,
      ...connectButtons('pc.drive.reconnect'));
  }

  /** The note of a folder picker without the app's API key (an administrator's shortcut). */
  function noKeyNote() {
    return noAppNote(ctx, () => layer, t('pc.drive.pickNoKey'));
  }

  /** « ou collez le lien du dossier » (« Tout le Drive » only). */
  function pastePart() {
    return h('div', { class: 'pc-drive-paste' }, pasteField,
      act('paste', t('pc.drive.pasteOk'), { variant: 'secondary', size: undefined, onClick: () => sendLink() }));
  }

  /** Safari blocks Google's cookies in its folder window: « Sélectionner » then spins for ever. */
  function safariNote() {
    if (!isSafari()) return null;
    return h('p', { class: 'pc-drive-safari' }, icon('alert', { size: 14 }), h('span', {}, t('pc.drive.safariHint')), infoTip(t('pc.drive.safariTip')));
  }

  /** Connected without a folder: step 1 done, step 2 big and obvious. */
  function folderSteps() {
    const ready = pickerReady();
    const full = view.mode === 'full';
    const who = [accountText(view.account), view.mode ? t(`pc.drive.modeName.${view.mode}`) : ''].filter(Boolean).join(' · ');
    return h('ol', { class: 'pc-drive-steps' },
      step(1, 'done', t('pc.drive.step1Done'), null, who ? line(who) : null),
      step(2, 'active', t('pc.drive.step2'), t(full ? 'pc.drive.pickTipFull' : 'pc.drive.pickTip'),
        h('div', { class: 'pc-drive-pick' },
          h('span', { class: 'pc-drive-pick-icon', 'aria-hidden': 'true' }, icon('folder', { size: 26 })),
          h('p', { class: 'pc-drive-pick-text' }, t('pc.drive.pickText')),
          act('pick', t('pc.drive.pick'), { variant: 'primary', size: undefined, icon: 'folder', disabled: busy || !ready, onClick: () => openPicker() }),
          // « Limité à un dossier » sees only the folder chosen: never invited to skip it.
          full && view.storage ? line(t('pc.drive.noFolderStorage', { name: OFFICE_FOLDER })) : null),
        ready ? safariNote() : noKeyNote(),
        full ? pastePart() : null));
  }

  /** Connected with its folder: what the office may see, calmly. */
  function summaryPart() {
    const folder = view.folder;
    const full = view.mode === 'full';
    const link = driveFolderLink(folder);
    const ready = pickerReady();
    const meta = [accountText(view.account), quotaText(view.quota)].filter(Boolean).join(' · ');
    return h('section', { class: ['pc-drive-summary', { 'is-full': full }] },
      h('span', { class: 'pc-drive-summary-icon', 'aria-hidden': 'true' }, icon(full ? 'folder' : 'lock', { size: 20 })),
      h('div', { class: 'pc-drive-summary-body' },
        h('p', { class: 'pc-drive-summary-title pc-drive-folder' }, `${t(full ? 'pc.drive.accessFull' : 'pc.drive.accessLimited')} `,
          h('strong', { class: 'pc-drive-folder-name' }, `« ${folder.name || folder.id} »`)),
        meta ? h('p', { class: 'pc-drive-summary-meta' }, meta) : null,
        view.created ? h('p', { class: 'pc-drive-summary-meta' }, t('pc.drive.created')) : null,
        h('div', { class: 'pc-drive-actions' },
          link ? extButton(link, t('pc.drive.openFolder')) : null,
          act('pick', t('pc.drive.changeFolder'), { variant: 'secondary', icon: 'folder', disabled: busy || !ready, onClick: () => openPicker() }),
          full && !pasteShown ? act('paste-show', t('pc.drive.pasteOther'), { variant: 'ghost', onClick: () => { pasteShown = true; render(); pasteInput.focus?.(); } }) : null),
        ready ? safariNote() : noKeyNote(),
        full && pasteShown ? pastePart() : null));
  }

  function storagePart() {
    if (!isAdmin()) {
      return view.storage ? h('section', { class: 'pc-drive-part' }, alertBox('info', t('pc.drive.storageMember'))) : null;
    }
    const other = otherStorage();
    const sw = switchToggle(t('pc.drive.storageSwitch'), {
      checked: view.storage, disabled: busy || (!view.storage && !view.connected), hint: t('pc.drive.storageHint'),
      onChange: (on) => setStorage(on),
    });
    keeper.reg(`drive-storage:${id}`, sw.input);
    return h('section', { class: 'pc-drive-part' },
      h('h5', { class: 'pc-drive-sub' }, h('span', { class: 'mono-label' }, t('pc.drive.storageTitle')), infoTip(t('pc.drive.storageTip'))),
      sw,
      !view.storage && other ? line(t('pc.drive.storageOther', { name: other.label })) : null,
      !view.storage && !view.connected ? line(t('pc.drive.storageNeedsConnection')) : null);
  }

  function transferPart() {
    const shown = view.storage || view.done.files || view.errors.length || view.trash;
    if (!shown) return null;
    const now = Date.now() + (Number(ctx.store?.get?.()?.serverOffset) || 0);
    const owner = (o) => (o === 'studio' || o === 'tasks' ? t(`pc.drive.owner.${o}`) : o);
    const admin = isAdmin();
    const done = doneText(view);
    const pending = pendingOf(view).files;
    return h('section', { class: 'pc-drive-part pc-drive-transfer' },
      h('h5', { class: 'pc-drive-sub' }, h('span', { class: 'mono-label' }, t('pc.drive.transfer')),
        infoTip(t('pc.drive.transferTip'), { label: t('pc.drive.tipAria', { name: t('pc.drive.transfer') }) })),
      view.storage ? h('p', { class: ['pc-drive-pending', { 'is-clear': !pending }] }, pendingText(view)) : null,
      view.sweeping
        ? h('p', { class: 'pc-drive-line is-busy' }, t('pc.drive.sweeping'))
        : view.lastSweepAt ? h('p', { class: 'muted pc-drive-line', title: formatDateTime(view.lastSweepAt, now) }, t('pc.drive.lastSweep', { when: relativeTime(view.lastSweepAt, now) })) : null,
      done ? line(done) : null,
      view.paused ? alertBox('info', t('pc.drive.pausedNote')) : null,
      view.lastError && view.connected ? alertBox('danger', t('pc.drive.lastError', { error: view.lastError })) : null,
      view.errors.length
        ? h('details', { class: 'pc-drive-errors' }, h('summary', {}, t('pc.drive.errors', { n: view.errors.length })),
          h('ul', {}, view.errors.map((e) => h('li', {}, e.owner ? h('span', { class: 'mono-label' }, `${owner(e.owner)} · `) : null, e.message))))
        : null,
      view.trash ? line(t('pc.drive.trash', { n: view.trash })) : null,
      admin && (view.storage || view.paused)
        ? h('div', { class: 'pc-drive-actions' },
          // Nothing left on the server: nothing to send now.
          view.storage && (pending || view.sweeping) ? act('sync', t('pc.drive.sync'), {
            variant: 'secondary', icon: 'upload', disabled: busy || view.paused || view.sweeping || !view.connected, onClick: () => sync(),
          }) : null,
          act('pause', t(view.paused ? 'pc.drive.resume' : 'pc.drive.pause'), {
            variant: 'secondary', icon: view.paused ? 'repeat' : 'clock', onClick: () => setPaused(!view.paused),
          }))
        : null);
  }

  /** Connected: « Changer l’accès » and « Reconnecter », small, at the bottom. */
  function morePart() {
    return h('div', { class: 'pc-drive-more' },
      h('span', { class: 'pc-drive-more-item' },
        act('change', t('pc.drive.change'), {
          variant: 'secondary', icon: 'repeat', onClick: () => { changing = true; modes.set(view.mode === 'full' ? 'limited' : 'full'); render(); },
        }), infoTip(t('pc.drive.changeTip'))),
      h('span', { class: 'pc-drive-more-item' },
        act('reconnect', t('pc.drive.reconnectSame'), { variant: 'secondary', icon: 'key', onClick: () => connect(view.mode || 'limited') }),
        infoTip(t('pc.drive.reconnectSameTip'))));
  }

  function render() {
    if (destroyed) return;
    const restore = keeper.begin();
    if (error && !view) {
      content.replaceChildren(alertBox('danger', errorText(error)),
        h('div', { class: 'pc-drive-actions' }, act('retry', t('pc.retry'), { variant: 'ghost', icon: 'refresh', onClick: () => { error = null; load(); } })));
      restore();
      return;
    }
    if (!view) return;
    const state = driveState(view);
    const connected = state === 'connected' || state === 'paused';
    let parts;
    if (connected && changing) parts = [changePart()];
    else if (!connected) parts = [setupSteps(state), view.storage ? storagePart() : null, transferPart()];
    // No folder yet: choosing it is the only task (the office's files and transfers come after).
    else if (!view.folder) parts = [folderSteps(), morePart()];
    else parts = [summaryPart(), storagePart(), transferPart(), morePart()];
    content.replaceChildren(...[headPart(state), ...parts].filter(Boolean));
    layer?.setSubtitle?.(subtitle());
    restore();
  }

  /** What the window is for, in one sentence: its steps until ready, then what the office may see. */
  function subtitle() {
    if (!view?.folder || !view.connected || changing) return t('pc.settings.subtitle.gdrive');
    return t(view.mode === 'full' ? 'pc.settings.subtitle.gdriveFull' : 'pc.settings.subtitle.gdriveLimited');
  }

  // --- data -------------------------------------------------------------------------------------
  function schedulePoll(ms = SWEEP_POLL_MS) {
    clearTimeout(pollTimer);
    pollTimer = null;
    if (destroyed || !view?.sweeping) return;
    pollTimer = setTimeout(() => refresh(), ms);
  }

  function take(next) {
    const first = !view;
    view = driveConnectorOf(next);
    error = null;
    // The access to (re)connect with starts from the connector's; a choice made here stays.
    if (first && !changing) modes.set(view.mode || conn.plugin?.mode || 'limited');
    if (view.label && view.label !== conn.label) layer?.setTitle?.(t('pc.drive.title', { name: view.label }));
    render();
    schedulePoll();
  }

  async function load() {
    try {
      const [next, library] = await Promise.all([
        ctx.api.get(`${path}/status`),
        ctx.api.get(LIBRARY_BASE).catch(() => null),
      ]);
      if (destroyed) return;
      if (library) lib = normalizeLibrary(library);
      take(next);
    } catch (err) {
      if (destroyed) return;
      error = err?.status === 404 ? new Error(t('pc.gone')) : err;
      render();
    }
  }

  /** The connector alone, again (after an action, while a transfer runs, the picker closed). */
  async function refresh() {
    try {
      const next = await ctx.api.get(`${path}/status`);
      if (!destroyed) take(next);
    } catch (err) {
      if (!destroyed && !view) { error = err; render(); }
    }
  }

  /** An action: one at a time, its error in a toast. */
  async function run(fn) {
    if (busy || destroyed) return;
    busy = true;
    render();
    try {
      await fn();
    } catch (err) {
      if (!destroyed) ctx.toast.error(errorText(err));
    } finally {
      busy = false;
      render();
    }
  }

  function connect(mode) {
    return run(async () => {
      const ok = await startGoogle(ctx, { id }, { mode, navigate: navigate || ctx.navigateTo || defaultNavigate });
      if (ok) layer?.close('done');
    });
  }

  async function setStorage(on) {
    if (busy || destroyed) return;
    const other = otherStorage();
    const ok = await ctx.overlay.confirm(on
      ? {
        title: t('pc.drive.storageOnTitle', { name: name() }),
        message: other ? `${t('pc.drive.storageOnText')} ${t('pc.drive.storageMoveText', { other: other.label, name: name() })}` : t('pc.drive.storageOnText'),
        confirmLabel: t('pc.drive.storageOnOk'),
      }
      : { title: t('pc.drive.storageOffTitle', { name: name() }), message: t('pc.drive.storageOffText'), confirmLabel: t('pc.drive.storageOffOk') });
    if (destroyed) return;
    if (!ok) {
      render(); // the switch shows the stored state again
      return;
    }
    await run(async () => {
      const next = await ctx.api.put(`${path}/storage`, { storage: on });
      if (destroyed) return;
      if (lib) lib = { ...lib, connectors: lib.connectors.map((c) => (c.catalogId === 'gdrive' ? { ...c, storage: on ? c.id === id : c.id === id ? false : c.storage } : c)) };
      take(next);
      ctx.toast.success(t(on ? 'pc.drive.storageOn' : 'pc.drive.storageOff', { name: name() }));
      changed();
    });
  }

  function sync() {
    return run(async () => {
      await ctx.api.post(`${path}/sync`, {});
      if (destroyed) return;
      ctx.toast.info(t('pc.drive.syncStarted', { name: name() }));
      view = { ...view, sweeping: true };
      schedulePoll(1000);
    });
  }

  function setPaused(paused) {
    return run(async () => {
      const next = await ctx.api.put(`${path}/pause`, { paused });
      if (destroyed) return;
      take(next);
      ctx.toast.success(t(paused ? 'pc.drive.paused' : 'pc.drive.resumed', { name: name() }));
    });
  }

  function sendLink() {
    const url = String(pasteInput.value ?? '').trim();
    pasteField.setError(null);
    if (!url) {
      pasteField.setError(t('pc.drive.pasteEmpty'));
      pasteInput.focus?.();
      return Promise.resolve();
    }
    return run(async () => {
      try {
        const next = await ctx.api.put(`${path}/folder`, { url });
        if (destroyed) return;
        pasteInput.value = '';
        pasteShown = false;
        take(next);
        ctx.toast.success(pickedText(view.folder?.name || '…', next?.moved));
        changed();
      } catch (err) {
        if (err?.status === 400) {
          if (!destroyed) pasteField.setError(errorText(err));
          return;
        }
        throw err;
      }
    });
  }

  /** The toast of a folder chosen: and, when the office's files followed it, how many items moved. */
  function pickedText(folder, moved) {
    const n = Number.isInteger(moved) && moved > 0 ? moved : 0;
    return n ? t('pc.drive.pickedMoved', { folder, n }) : t('pc.drive.picked', { folder, name: name() });
  }

  // --- Google's picker in a popup ---------------------------------------------------------------
  /** The picker page's message: the folder saved (or the window closed without one). */
  function onMessage(ev) {
    const data = ev?.data;
    if (!isObject(data) || data.type !== PICKER_MESSAGE || data.connectorId !== id) return;
    const here = String(globalThis.location?.origin || '');
    if (here && ev.origin !== here) return;
    if (popup && ev.source && ev.source !== popup) return;
    stopPopup();
    if (data.outcome === 'picked') {
      const folder = isObject(data.folder) && typeof data.folder.name === 'string' ? data.folder.name.slice(0, 250) : '';
      ctx.toast.success(pickedText(folder || '…', data.moved));
      changed();
    }
    refresh();
  }

  function stopPopup() {
    clearInterval(popupTimer);
    popupTimer = null;
    popup = null;
  }

  function openPicker() {
    if (!pickerReady() || busy) return;
    const w = (openWindow || ctx.openWindow || defaultOpenWindow)(`${path}/picker`);
    if (!w) {
      ctx.toast.warn(t('pc.drive.popupBlocked'));
      return;
    }
    stopPopup();
    popup = w;
    w.focus?.();
    // Closed without a message (refused, closed by hand): the connector is read again.
    popupTimer = setInterval(() => {
      if (!popup || popup.closed) {
        stopPopup();
        if (!destroyed) refresh();
      }
    }, POPUP_POLL_MS);
  }

  const close = button(t('common.close'), { variant: 'ghost', onClick: () => layer?.close('button') });
  layer = ctx.overlay.openModal({
    title: t('pc.drive.title', { name: conn.label || id }), subtitle: t('pc.settings.subtitle.gdrive'), size: 'md',
    content, footer: [close], className: 'adm-modal pc-modal pc-plugin-modal pc-drive-modal',
  });
  layer.onClose?.(() => {
    destroyed = true;
    clearTimeout(pollTimer);
    stopPopup();
    globalThis.removeEventListener?.('message', onMessage);
  });
  globalThis.addEventListener?.('message', onMessage);
  load();
  return layer;
}
